import { IconButton, LuIcon } from "@overmap-ai/blocks"
import { isImageFile } from "@overmap-ai/core"
import type { UUIDFile, UUIDPromise } from "@overmap-ai/forms"
import { memo, type MouseEventHandler, useCallback } from "react"

import { useResolvePromise } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { FileThumbnail } from "../Thumbnails/FileThumbnail"
import { ImageFileThumbnail } from "../Thumbnails/ImageFileThumbnail"
import { VideoFileThumbnail } from "../Thumbnails/VideoFileThumbnail"

interface CommentFileThumbnailListItemProps {
	file: UUIDFile | UUIDPromise<UUIDFile>
	onDelete: (file: UUIDFile | UUIDPromise<UUIDFile>) => void
}

export const CommentFileThumbnailListItem = memo((props: CommentFileThumbnailListItemProps) => {
	const { file, onDelete } = props

	const [resolvedFile, error] = useResolvePromise<UUIDFile, UUIDPromise<UUIDFile>>(file)

	const handleDelete = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation()
			e.preventDefault()

			onDelete(file)
		},
		[file, onDelete],
	)

	const renderContent = () => {
		if (resolvedFile && isImageFile(resolvedFile)) {
			return <ImageFileThumbnail file={resolvedFile} errored={!!error} />
		}

		if (resolvedFile?.type.startsWith("video/")) {
			return <VideoFileThumbnail file={resolvedFile} errored={!!error} />
		}

		return <FileThumbnail file={resolvedFile} errored={!!error} />
	}

	return (
		<div className="relative size-max">
			{renderContent()}
			<IconButton
				className="size-4! absolute right-0 top-0 -translate-x-[-25%] translate-y-[-25%]"
				type="button"
				radius="full"
				size="xs"
				variant="solid"
				accentColor="base"
				onClick={handleDelete}
			>
				<LuIcon icon={modelIconMapping.close} />
			</IconButton>
		</div>
	)
})
CommentFileThumbnailListItem.displayName = "CommentFileThumbnailListItem"
